import { Controller } from "@hotwired/stimulus";
import Sortable from "sortablejs";

// GR-3394: the quote form's line rows reorder by drag and drop.
//
// They rendered in id order, which is the order they were typed in, so a room
// block typed after the catering stayed under it for the life of the quote.
//
// What it does NOT do is post a reorder. The two sortables it is modelled on
// (tiered_list_sortable, app_nav_sortable) both PATCH the new position at the
// server, because in both cases every row is a saved record with an id. Here
// the rows on a new quote are GR-3352's prefill and exist only in memory: there
// is nothing to name in a request and no quote to name it against. So the order
// is a hidden position per row, renumbered here and saved with the quote
// through the path the form already has.
//
// GR-1777's keyboard fallback comes with it, for the same reason it was written
// there: SortableJS answers a mouse and nothing else, which would leave the
// order unreachable by keyboard.
export default class QuoteLineSortableController extends Controller {
  static ROW = ".quote-line";
  static HANDLE = "[data-drag-handle='line']";

  connect() {
    this.grabbedRow = null;
    this.originalOrder = null;
    this.liveRegion = this.createLiveRegion();

    this.sortable = Sortable.create(this.element, {
      animation: 150,
      draggable: this.constructor.ROW,
      handle: this.constructor.HANDLE,
      // A persisted row that has been removed is hidden rather than detached,
      // so the server hears about the deletion. It is not a row to move.
      filter: "[hidden]",
      ghostClass: "sortable-ghost",
      chosenClass: "sortable-chosen",
      dragClass: "sortable-drag",
      onEnd: () => this.renumber(),
    });

    this.keydownHandler = this.handleKeydown.bind(this);
    this.element.addEventListener("keydown", this.keydownHandler);

    // Rows arrive and leave without passing through here: Add Line clones the
    // template, the Request Context sidebar asks for a row (GR-3355), and the
    // bin takes one away. Each of those changes what the positions should be,
    // and none of them is this controller's to intercept.
    this.observer = new MutationObserver(() => this.refresh());
    this.observer.observe(this.element, { childList: true });

    this.refresh();
  }

  // Everything that has to be true of every row on screen: what it posts as its
  // position, and that its grip says what it is for.
  refresh() {
    this.renumber();
    this.describeHandles();
  }

  disconnect() {
    this.sortable?.destroy();
    this.sortable = null;
    this.observer?.disconnect();
    this.element.removeEventListener("keydown", this.keydownHandler);
    this.liveRegion?.remove();
    this.liveRegion = null;
  }

  // The order on screen, written into the fields that post it. One to n rather
  // than nought to n-1 because a nought position and no position at all are the
  // same integer to read past, and the column is nullable on purpose.
  //
  // Only the rows still on screen are numbered: a row marked for destruction
  // holds a place in a quote that is about to lose it.
  renumber() {
    this.rows().forEach((row, index) => {
      const field = this.positionField(row);
      if (field) field.value = String(index + 1);
    });
  }

  rows() {
    return Array.from(this.element.querySelectorAll(this.constructor.ROW)).filter(
      (row) => !row.hidden
    );
  }

  positionField(row) {
    return row.querySelector("input[name$='[position]']");
  }

  handle(row) {
    return row.querySelector(this.constructor.HANDLE);
  }

  // --- Keyboard (GR-1777's pattern) ----------------------------------------

  describeHandles() {
    this.rows().forEach((row) => {
      this.handle(row)?.setAttribute(
        "aria-roledescription",
        "Draggable line: press Space to pick up, then Arrow keys to move"
      );
    });
  }

  handleKeydown(event) {
    const row = event.target.closest(this.constructor.ROW);
    if (!row || !this.handle(row)?.contains(event.target)) return;

    const key = event.key;

    if (key === " " || key === "Enter") {
      event.preventDefault();
      this.grabbedRow === row ? this.dropRow() : this.grabRow(row);
      return;
    }

    if (this.grabbedRow !== row) return;

    if (key === "ArrowDown") {
      event.preventDefault();
      this.move(row, "down");
    } else if (key === "ArrowUp") {
      event.preventDefault();
      this.move(row, "up");
    } else if (key === "Escape") {
      event.preventDefault();
      this.cancelGrab();
    }
  }

  grabRow(row) {
    this.grabbedRow = row;
    this.originalOrder = this.rows();
    // aria-grabbed is deprecated in ARIA 1.1 and still announced by the major
    // screen readers; the data attribute beside it is what CSS can key off.
    this.handle(row).setAttribute("aria-grabbed", "true");
    row.dataset.grabbed = "true";
    this.announce(
      `Picked up ${this.rowLabel(row)}. Use Arrow Up and Arrow Down to move it, ` +
        "Space to drop it, Escape to cancel."
    );
  }

  move(row, direction) {
    const rows = this.rows();
    const target = rows[rows.indexOf(row) + (direction === "down" ? 1 : -1)];
    if (!target) return;

    if (direction === "down") {
      this.element.insertBefore(target, row);
    } else {
      this.element.insertBefore(row, target);
    }

    this.renumber();
    this.announce(`${this.rowLabel(row)} moved ${direction} to position ${this.positionOf(row)}.`);
  }

  dropRow() {
    const row = this.grabbedRow;
    if (!row) return;

    this.release(row);
    this.renumber();
    this.announce(`Dropped ${this.rowLabel(row)} at position ${this.positionOf(row)}.`);
  }

  cancelGrab() {
    const row = this.grabbedRow;
    if (!row) return;

    this.originalOrder.forEach((node) => this.element.appendChild(node));
    this.release(row);
    this.renumber();
    this.announce(`Cancelled. ${this.rowLabel(row)} is back where it was.`);
  }

  release(row) {
    this.handle(row)?.removeAttribute("aria-grabbed");
    delete row.dataset.grabbed;
    this.grabbedRow = null;
    this.originalOrder = null;
  }

  positionOf(row) {
    return this.rows().indexOf(row) + 1;
  }

  // What the row is called, for the announcement. Its description if it has
  // one, because that is what a person put there; its place in the quote if it
  // does not, because a blank row still has to be referrable to.
  rowLabel(row) {
    const description = row.querySelector("input[name$='[description]']")?.value?.trim();
    return description ? `line "${description}"` : `line ${this.positionOf(row)}`;
  }

  createLiveRegion() {
    const node = document.createElement("div");
    node.setAttribute("aria-live", "polite");
    node.setAttribute("aria-atomic", "true");
    node.setAttribute("data-quote-line-sortable-live-region", "true");
    node.classList.add("sr-only");
    this.element.insertAdjacentElement("afterend", node);
    return node;
  }

  announce(message) {
    if (this.liveRegion) this.liveRegion.textContent = message;
  }
}
